import {isEqual, pickBy} from 'lodash';

import {isEnumType} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {ActionWithPayload} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsFilterRelationsEnum,
  AnalyticsFilterTypesEnum,
  AnalyticsInboxScopeFiltersEnum,
  AnalyticsTagModesEnum,
} from '../../../../models/api/analyticsSavedViewModel';
import {ThunkAction} from '../../../appThunk';
import {isAnalyticsLoaded} from '../analyticsPanelSelectors';
import {fetchVisibleMetricsForReport} from '../report/analyticsPanelReportActions';
import {getAnalyticsFilters} from './analyticsPanelFiltersSelectors';
import {ANALYTICS_FILTERS_TAG_MODE, ANALYTICS_FILTERS_TAG_RELATION} from './analyticsPanelFiltersState';

/*
 * Type enum.
 */

export enum AnalyticsPanelFiltersActionsEnum {
  UPDATE_FILTERS = '@@panels/analytics/filters/UPDATE_FILTERS',
}

/*
 * Types.
 */

interface UpdateFiltersPayload {
  [AnalyticsFilterTypesEnum.TAGS]?: ReadonlyArray<number>;
  [AnalyticsFilterTypesEnum.EXCLUDE_TAGS]?: ReadonlyArray<number>;
  [AnalyticsFilterTypesEnum.WITHOUT_TAGS]?: boolean;
  [ANALYTICS_FILTERS_TAG_RELATION]?: AnalyticsFilterRelationsEnum;
  [ANALYTICS_FILTERS_TAG_MODE]?: AnalyticsTagModesEnum;
  [AnalyticsFilterTypesEnum.INBOXES]?: ReadonlyArray<number> | AnalyticsInboxScopeFiltersEnum;
  [AnalyticsFilterTypesEnum.TEAMMATES]?: ReadonlyArray<number>;
  [AnalyticsFilterTypesEnum.CHANNELS]?: ReadonlyArray<number>;
  [AnalyticsFilterTypesEnum.ACCOUNTS]?: ReadonlyArray<number>;
}

export interface UpdateFiltersAction extends ActionWithPayload<UpdateFiltersPayload> {
  type: AnalyticsPanelFiltersActionsEnum.UPDATE_FILTERS;
}

/*
 * Action creators.
 */

export function updateFiltersAction(filtersUpdate: UpdateFiltersPayload): ThunkAction {
  return (dispatch, getState) => {
    // State.
    const previousState = getState();
    const previousFilters = getAnalyticsFilters(previousState);
    const sanitizedPreviousFilters = sanitizeFilters(previousFilters);

    // Update filters in the store.
    dispatch({
      type: AnalyticsPanelFiltersActionsEnum.UPDATE_FILTERS,
      payload: filtersUpdate,
    });

    // If filters changed, we may need to update report data.
    const currentState = getState();
    const newFilters = getAnalyticsFilters(currentState);
    const sanitizedNewFilters = sanitizeFilters(newFilters);

    const hasNewFilterValues = !isEqual(sanitizedPreviousFilters, sanitizedNewFilters);

    const isLoaded = isAnalyticsLoaded(currentState); // Don't fetch the metrics if the panel isn't fully loaded yet.

    if (hasNewFilterValues && isLoaded) {
      // Update the report data if filters are changing. This is triggered when updating filters but also the selected view.
      dispatch(fetchVisibleMetricsForReport());
    }
  };
}

/*
 * Helpers.
 */

// Removes empty filters (filters that hold no values).
function sanitizeFilters(filters: UpdateFiltersPayload) {
  return pickBy(filters, (value, type) => {
    if (type === AnalyticsFilterTypesEnum.WITHOUT_TAGS) {
      return true;
    }

    if (
      (type === ANALYTICS_FILTERS_TAG_RELATION || type === ANALYTICS_FILTERS_TAG_MODE) &&
      filters[AnalyticsFilterTypesEnum.TAGS]?.length
    ) {
      return true;
    } // Keeping the tag mode and relation as "actives" only if we have tags selected.

    if (type === AnalyticsFilterTypesEnum.INBOXES && isEnumType(value, AnalyticsInboxScopeFiltersEnum)) {
      return true;
    }

    return Array.isArray(value) && value.length;
  });
}

/*
 * Union type.
 */

export type AnalyticsPanelFiltersAction = UpdateFiltersAction;
